import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Button as DsButton,
  Checkbox,
  InputField as DsInputField,
  LinkButton,
  Modal,
  Select,
  SelectionBox,
} from '@enamelai/design-system'
import { SchemaFormRenderer } from '@components/pages/FormBuilder/Shared/SchemaFormRenderer'
import KioskPatientRegistration from '@components/pages/FormBuilder/KioskPatientRegistration.component'
import {
  emptyAssignmentsView,
  kioskSubmitOutcome,
  type KioskIdentificationMode,
} from '@components/pages/FormBuilder/kioskPatientRegistration.logic'
import type {
  SchemaFormFieldValue,
  SchemaFormFieldValues,
} from '@components/pages/FormBuilder/Shared/SchemaFormRenderer'
import type { GroupField } from '@components/pages/FormBuilder/types'
import { validateSchemaFormSubmission } from '@components/pages/FormBuilder/Shared/SchemaFormRenderer/schemaFormValidation'
import {
  completeKioskAssignment,
  getKioskAssignments,
  prepareNewPatientKioskAssignments,
  type KioskAssignmentItem,
  type KioskAuditContext,
} from '@services/api/forms.api'
import { verifyOtp } from '@services/api/otp.api'
import {
  schemaJsonToGroups,
  formDataFieldsFromSchemaAndValues,
} from '@components/pages/FormBuilder/SendingFormsTable/formTemplateToFormData'
import { getClinicContactPhone } from '@utils/clinic.util'
import { useIdleReset } from '@hooks/useIdleReset'
import ConfirmationModal from '@components/common/ConfirmationModal/ConfirmationModal.component'
import {
  activateKioskBindingFromStaffSession,
  activateKioskWithStaffLogin,
  clearKioskActivationSession,
  clearKioskDeviceSession,
  clearKioskPatientSession,
  completeKioskActivation,
  getKioskActivationSession,
  getKioskBinding,
  getKioskPatientSession,
  isKioskDeviceMode,
  resolveKioskClinicContext,
  setKioskBinding,
  setKioskActivationSession,
  setKioskDeviceMode,
  setKioskPatientSession,
  type KioskClinicBinding,
  type KioskClinicSelectionRequired,
} from '@utils/kioskFormsUrl.util'
import checkmarkIcon from '@assets/images/Kiosk/checkmark.svg'
import progressActivityIcon from '@assets/images/Kiosk/progress-activity.svg'
import './KioskForm.css'
import { getAccessToken } from '@services/auth/accessTokenStore'

const TERMS_VERSION = '1.0'

/** Temporary: when true, skip consent/information screen and go straight to forms after OTP. */
const SKIP_CONSENT_SCREEN = true

const isValidEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())

/** One kiosk form entry: schema-native groups + field-id value map. */
export interface KioskFormEntry {
  assignmentId: string
  clinicId: string
  appointmentId: string | null
  name: string
  formTemplateId: string
  groups: GroupField[]
  fieldValues: SchemaFormFieldValues
  /** Patient full name from backend (for signature block). */
  patientName?: string
}

/** Build initial fieldValues from schema by seeding Rich Text fields with their placeholder text. */
function initialFieldValuesFromGroups(
  groups: GroupField[],
): SchemaFormFieldValues {
  const values: SchemaFormFieldValues = {}

  function walk(items: GroupField['fields'][number][]): void {
    for (const item of items) {
      if (
        'type' in item &&
        (item as { type: string }).type === 'Form Row' &&
        'fields' in item
      ) {
        walk((item as { fields: GroupField['fields'] }).fields)
      } else if ('name' in item && 'fields' in item) {
        walk((item as GroupField).fields)
      } else if ('id' in item && 'type' in item) {
        const field = item as {
          id: string
          type: string
          placeholder?: string
        }
        if (
          field.type === 'Rich Text' &&
          field.placeholder &&
          field.placeholder.length > 0
        ) {
          values[field.id] = field.placeholder
        }
      }
    }
  }

  for (const group of groups) {
    if (Array.isArray(group.fields)) {
      walk(group.fields)
    }
  }

  return values
}

/** Build form entries from assignments. Uses backend initial_field_values when present (autofill from patient at kiosk load). */
function assignmentsToFormEntries(
  assignments: KioskAssignmentItem[],
): KioskFormEntry[] {
  return assignments.map((a) => {
    const groups = schemaJsonToGroups(
      a.schema_json as
        | string
        | GroupField[]
        | Record<string, unknown>
        | null
        | undefined,
    )
    const baseValues = initialFieldValuesFromGroups(groups)
    const autofillValues = a.initial_field_values ?? {}
    const fieldValues: SchemaFormFieldValues = {
      ...baseValues,
      ...autofillValues,
    }
    return {
      assignmentId: a.assignmentId,
      clinicId: a.clinicId,
      appointmentId: a.appointmentId,
      name: a.name,
      formTemplateId: a.formTemplateId,
      groups,
      fieldValues,
      patientName: a.patient_name,
    }
  })
}

type KioskView =
  | 'setup'
  | 'patient-type'
  | 'new-patient'
  | 'preparing-forms'
  | 'assignment-error'
  | 'registration-complete'
  | 'otp'
  | 'consent'
  | 'forms'
type SubmitPhase = null | 'submitting' | 'success' | 'partial' | 'error'

export interface KioskConsentData {
  firstName: string
  lastName: string
  email: string
  agreedToTerms: boolean
  acceptedAt: string
  termsVersion: string
}

const OTP_BOX_COUNT = 6
const IDLE_RESET_MS = 5 * 60 * 1000

function isEmptyFieldValue(value: SchemaFormFieldValue | undefined): boolean {
  if (value == null || value === false) return true
  if (value === true) return false
  if (Array.isArray(value)) return value.length === 0
  return value.trim().length === 0
}

function fieldValuesEqual(
  left: SchemaFormFieldValue | undefined,
  right: SchemaFormFieldValue | undefined,
): boolean {
  if (isEmptyFieldValue(left) && isEmptyFieldValue(right)) return true
  if (Array.isArray(left) || Array.isArray(right)) {
    const leftValues = Array.isArray(left) ? left : []
    const rightValues = Array.isArray(right) ? right : []
    return (
      leftValues.length === rightValues.length &&
      leftValues.every((item, index) => item === rightValues[index])
    )
  }
  return left === right
}

const KioskForm: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [clinicBinding, setClinicBinding] = useState<KioskClinicBinding | null>(
    () => getKioskBinding(),
  )
  const clinicContext = useMemo(
    () => clinicBinding ?? resolveKioskClinicContext(),
    [clinicBinding],
  )
  const isKioskBound = clinicContext.dbName.trim() !== ''

  const [view, setView] = useState<KioskView>(() => {
    if (getKioskBinding() == null) return 'setup'
    return getKioskPatientSession() != null ? 'forms' : 'patient-type'
  })
  const [registrationNotice, setRegistrationNotice] = useState('')
  const [identificationMode, setIdentificationMode] =
    useState<KioskIdentificationMode>(
      () => getKioskPatientSession()?.identificationMode ?? null,
    )
  const [registrationAccessToken, setRegistrationAccessToken] = useState(
    () => getKioskActivationSession()?.accessToken ?? '',
  )
  const [registrationActorId, setRegistrationActorId] = useState(
    () => getKioskActivationSession()?.actorId ?? '',
  )
  const isKioskReady =
    isKioskBound &&
    registrationAccessToken.trim() !== '' &&
    registrationActorId.trim() !== ''
  const [setupUsername, setSetupUsername] = useState('')
  const [setupPassword, setSetupPassword] = useState('')
  const [setupError, setSetupError] = useState('')
  const [setupLoading, setSetupLoading] = useState(false)
  const [pendingClinicActivation, setPendingClinicActivation] =
    useState<KioskClinicSelectionRequired | null>(null)
  const [setupClinicId, setSetupClinicId] = useState('')

  const [codeDigits, setCodeDigits] = useState<string[]>(() =>
    Array.from({ length: OTP_BOX_COUNT }, () => ''),
  )
  const [verifyError, setVerifyError] = useState<string>('')
  const [verifying, setVerifying] = useState(false)
  const [kioskAccessToken, setKioskAccessToken] = useState<string | null>(
    () => getKioskPatientSession()?.accessToken ?? null,
  )
  const [kioskPatientId, setKioskPatientId] = useState<string>(
    () => getKioskPatientSession()?.patientId ?? '',
  )
  const [createdPatientId, setCreatedPatientId] = useState<string>('')
  const [assignmentsLoading, setAssignmentsLoading] = useState(false)
  const [assignmentsError, setAssignmentsError] = useState<string | null>(null)
  const assignmentsFetchedRef = useRef(false)
  const [sidepanelOpen, setSidepanelOpen] = useState(true)
  const [selectedFormId, setSelectedFormId] = useState<string | null>(null)
  const [visitedAssignmentIds, setVisitedAssignmentIds] = useState<Set<string>>(
    () => new Set(),
  )
  const [submitPhase, setSubmitPhase] = useState<SubmitPhase>(null)
  const [submitMessage, setSubmitMessage] = useState<string>('')
  const [formEntries, setFormEntries] = useState<KioskFormEntry[]>([])
  const [allDone, setAllDone] = useState(false)
  const [showFormValidation, setShowFormValidation] = useState(false)
  const [kioskClinicId, setKioskClinicId] = useState<string | null>(null)
  const [clinicContactPhone, setClinicContactPhone] = useState('')
  const [kioskClinicName, setKioskClinicName] = useState('')
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false)
  const initialFieldValuesRef = useRef<Record<string, SchemaFormFieldValues>>(
    {},
  )
  const successfulSubmissionCountRef = useRef(0)
  const failedSubmissionCountRef = useRef(0)

  const [consentFirstName, setConsentFirstName] = useState('')
  const [consentLastName, setConsentLastName] = useState('')
  const [consentEmail, setConsentEmail] = useState('')
  const [consentAgreedToTerms, setConsentAgreedToTerms] = useState(false)
  const [consentData, setConsentData] = useState<KioskConsentData | null>(null)
  const [consentErrors, setConsentErrors] = useState<{
    firstName?: string
    lastName?: string
    email?: string
    terms?: string
  }>({})
  const [termsModalOpen, setTermsModalOpen] = useState(false)

  const staffSessionAvailable = useMemo((): boolean => {
    const token = (getAccessToken() ?? '').trim()
    const dbName = localStorage.getItem('db_name')?.trim() ?? ''
    return token !== '' && dbName !== ''
  }, [])

  // The clinic binding survives kiosk-window restarts; the route lock is scoped
  // to this tab so staff tabs on the same origin remain usable.
  useEffect(() => {
    if (isKioskBound) setKioskDeviceMode()
  }, [isKioskBound])

  // Migrate legacy ?dbName=&clinicId= links into device binding, then strip query.
  useEffect(() => {
    const dbNameFromUrl = searchParams.get('dbName')?.trim() ?? ''
    if (dbNameFromUrl === '') return
    if (getKioskBinding() == null) {
      setKioskBinding({
        dbName: dbNameFromUrl,
        clinicId: searchParams.get('clinicId')?.trim() ?? '',
      })
      setClinicBinding(getKioskBinding())
      setView('patient-type')
    }
    navigate('/forms/kiosk', { replace: true })
  }, [navigate, searchParams])

  const applyBinding = useCallback(
    (
      binding: KioskClinicBinding,
      registrationSession?: { accessToken: string; actorId: string },
    ): void => {
      setClinicBinding(binding)
      setKioskDeviceMode()
      if (registrationSession) {
        setKioskActivationSession(registrationSession)
        setRegistrationAccessToken(registrationSession.accessToken)
        setRegistrationActorId(registrationSession.actorId)
      } else {
        clearKioskActivationSession()
        setRegistrationAccessToken('')
        setRegistrationActorId('')
      }
      setSetupError('')
      setView('patient-type')
    },
    [],
  )

  const handleActivateFromStaffSession = useCallback((): void => {
    if (!activateKioskBindingFromStaffSession()) {
      setSetupError(
        'No staff clinic session found. Sign in below to activate this kiosk.',
      )
      return
    }
    const binding = getKioskBinding()
    if (binding == null) {
      setSetupError('Unable to activate kiosk from this session.')
      return
    }
    const accessToken = getAccessToken() ?? ''
    let actorId = ''
    try {
      actorId = JSON.parse(localStorage.getItem('user') || '{}').user_id ?? ''
    } catch {
      actorId = ''
    }
    applyBinding(
      binding,
      accessToken && actorId ? { accessToken, actorId } : undefined,
    )
  }, [applyBinding])

  const handleSetupLogin = useCallback((): void => {
    if (!setupUsername.trim() || !setupPassword) {
      setSetupError('Enter staff username and password.')
      return
    }
    setSetupLoading(true)
    setSetupError('')
    activateKioskWithStaffLogin({
      username: setupUsername,
      password: setupPassword,
    })
      .then((result) => {
        setSetupPassword('')
        if ('requiresClinicSelection' in result) {
          setPendingClinicActivation(result)
          setSetupClinicId(result.suggestedClinicId)
          return
        }
        applyBinding(result.binding, {
          accessToken: result.accessToken,
          actorId: result.actorId,
        })
      })
      .catch((err: { message?: string }) => {
        setSetupError(
          err?.message ?? 'Sign-in failed. Check credentials and try again.',
        )
      })
      .finally(() => {
        setSetupLoading(false)
      })
  }, [applyBinding, setupPassword, setupUsername])

  const handleClinicActivation = useCallback((): void => {
    if (!pendingClinicActivation || !setupClinicId) {
      setSetupError('Select a clinic to continue.')
      return
    }
    try {
      const result = completeKioskActivation({
        ...pendingClinicActivation,
        clinicId: setupClinicId,
      })
      setPendingClinicActivation(null)
      setSetupClinicId('')
      applyBinding(result.binding, {
        accessToken: result.accessToken,
        actorId: result.actorId,
      })
    } catch (error) {
      setSetupError(
        error instanceof Error ? error.message : 'Unable to select clinic.',
      )
    }
  }, [applyBinding, pendingClinicActivation, setupClinicId])

  const handleLogoutKiosk = useCallback((): void => {
    clearKioskDeviceSession()
    setClinicBinding(null)
    setRegistrationAccessToken('')
    setRegistrationActorId('')
    setPendingClinicActivation(null)
    setSetupClinicId('')
    setKioskAccessToken(null)
    setKioskPatientId('')
    setCodeDigits(Array.from({ length: OTP_BOX_COUNT }, () => ''))
    setVerifyError('')
    setView('setup')
  }, [])

  const prepareRegistrationForms = useCallback(
    async (patientId: string): Promise<void> => {
      setView('preparing-forms')
      setIdentificationMode('new')
      setKioskPatientId(patientId)
      setCreatedPatientId(patientId)
      setKioskAccessToken(registrationAccessToken)
      assignmentsFetchedRef.current = false
      setVisitedAssignmentIds(new Set())
      setShowFormValidation(false)
      try {
        const result = await prepareNewPatientKioskAssignments(
          registrationAccessToken,
          patientId,
          clinicContext,
        )
        if (!result.configured) {
          setView('registration-complete')
          return
        }
        setKioskPatientSession({
          accessToken: registrationAccessToken,
          patientId,
          identificationMode: 'new',
        })
        setView(SKIP_CONSENT_SCREEN ? 'forms' : 'consent')
      } catch {
        setView('assignment-error')
      }
    },
    [clinicContext, registrationAccessToken],
  )

  const handleRegistrationCreated = useCallback(
    (patientId: string): void => {
      void prepareRegistrationForms(patientId)
    },
    [prepareRegistrationForms],
  )

  const handleNewPatientChoice = useCallback((): void => {
    if (!registrationAccessToken || !registrationActorId) {
      setRegistrationNotice(
        'New-patient registration needs staff assistance. Please ask the front desk to reactivate this kiosk.',
      )
      return
    }
    if (!clinicContext.clinicId.trim()) {
      setRegistrationNotice(
        'This kiosk is not linked to a clinic. Please ask the front desk for assistance.',
      )
      return
    }
    setRegistrationNotice('')
    setView('new-patient')
  }, [clinicContext.clinicId, registrationAccessToken, registrationActorId])

  const handleRegistrationDuplicate = useCallback((): void => {
    setRegistrationNotice(
      'A matching patient record may already exist. For your privacy, no account details are shown. Please use your existing-patient code or ask the front desk for help.',
    )
    setView('otp')
  }, [])

  const handleCodeDigitChange = useCallback((index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1)
    setCodeDigits((prev) => {
      const next = [...prev]
      next[index] = digit
      if (digit && index < OTP_BOX_COUNT - 1) {
        const el = document.querySelector(
          `input[name="kiosk-otp-${index + 1}"]`,
        ) as HTMLInputElement | null
        el?.focus()
      }
      return next
    })
    setVerifyError('')
  }, [])

  const handleCodeKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault()
        setCodeDigits((prev) => {
          const next = [...prev]
          next[index] = e.key
          return next
        })
        setVerifyError('')
        if (index < OTP_BOX_COUNT - 1) {
          const el = document.querySelector(
            `input[name="kiosk-otp-${index + 1}"]`,
          ) as HTMLInputElement | null
          el?.focus()
        }
        return
      }
      if (e.key === 'Backspace' && !codeDigits[index] && index > 0) {
        const el = document.querySelector(
          `input[name="kiosk-otp-${index - 1}"]`,
        ) as HTMLInputElement | null
        el?.focus()
      }
    },
    [codeDigits],
  )

  const handleCodePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault()
      const raw = e.clipboardData.getData('text/plain')
      const digits = raw.replace(/\D/g, '').slice(0, OTP_BOX_COUNT).split('')
      if (digits.length === 0) return
      setCodeDigits((prev) => {
        const next = [...prev]
        digits.forEach((d, i) => {
          if (i < OTP_BOX_COUNT) next[i] = d
        })
        return next
      })
      setVerifyError('')
      const focusIndex = Math.min(digits.length, OTP_BOX_COUNT) - 1
      setTimeout(() => {
        const el = document.querySelector(
          `input[name="kiosk-otp-${focusIndex}"]`,
        ) as HTMLInputElement | null
        el?.focus()
      }, 0)
    },
    [],
  )

  const handleVerifySubmit = useCallback(() => {
    const code = codeDigits.join('').trim()
    if (code.length !== OTP_BOX_COUNT) {
      setVerifyError('Please enter the full 6-digit code.')
      return
    }
    const { dbName } = clinicContext
    if (!dbName) {
      setVerifyError(
        'This kiosk is not activated for a clinic. Ask staff to complete kiosk setup.',
      )
      setView('setup')
      return
    }
    setVerifying(true)
    setVerifyError('')
    verifyOtp({
      code,
      db_name: dbName,
    })
      .then((result) => {
        // A patient may enter a new OTP after returning from a previous forms
        // session. Always invalidate the prior assignment fetch so direct
        // requests created since that session are loaded.
        assignmentsFetchedRef.current = false
        setFormEntries([])
        setAssignmentsError(null)
        setSelectedFormId(null)
        setVisitedAssignmentIds(new Set())
        setShowFormValidation(false)
        initialFieldValuesRef.current = {}
        setKioskAccessToken(result.access_token)
        setIdentificationMode('existing')
        if (result.patient_id) {
          setKioskPatientId(result.patient_id)
          setKioskPatientSession({
            accessToken: result.access_token,
            patientId: result.patient_id,
            identificationMode: 'existing',
          })
        }
        setView(SKIP_CONSENT_SCREEN ? 'forms' : 'consent')
      })
      .catch(
        (err: {
          response?: { status?: number; data?: { message?: string } }
          message?: string
        }) => {
          if (err?.response?.status === 401 || err?.response?.status === 403) {
            clearKioskPatientSession()
            setKioskAccessToken(null)
            setKioskPatientId('')
            setIdentificationMode(null)
            assignmentsFetchedRef.current = false
            setVerifyError(
              'Your session expired. Please enter a new six-digit code.',
            )
            setView('otp')
            return
          }
          const msg =
            err?.response?.data?.message ??
            err?.message ??
            'Invalid or expired OTP. Please try again or ask staff for a new code.'
          setVerifyError(msg)
        },
      )
      .finally(() => {
        setVerifying(false)
      })
  }, [clinicContext, codeDigits])

  const consentFormValid = useMemo((): boolean => {
    const fn = consentFirstName.trim()
    const ln = consentLastName.trim()
    const em = consentEmail.trim()
    return (
      fn.length > 0 && ln.length > 0 && isValidEmail(em) && consentAgreedToTerms
    )
  }, [consentFirstName, consentLastName, consentEmail, consentAgreedToTerms])

  const handleConsentContinue = useCallback((): void => {
    const fn = consentFirstName.trim()
    const ln = consentLastName.trim()
    const em = consentEmail.trim()
    const errors: {
      firstName?: string
      lastName?: string
      email?: string
      terms?: string
    } = {}
    if (fn.length === 0) errors.firstName = 'First name is required.'
    if (ln.length === 0) errors.lastName = 'Last name is required.'
    if (em.length === 0) {
      errors.email = 'Email is required.'
    } else if (!isValidEmail(em)) {
      errors.email = 'Please enter a valid email address.'
    }
    if (!consentAgreedToTerms)
      errors.terms = 'You must agree to the Terms and Conditions.'
    setConsentErrors(errors)
    if (Object.keys(errors).length > 0) return

    const acceptedAt = new Date().toISOString()
    const data: KioskConsentData = {
      firstName: fn,
      lastName: ln,
      email: em,
      agreedToTerms: true,
      acceptedAt,
      termsVersion: TERMS_VERSION,
    }
    setConsentData(data)
    setView('forms')
  }, [consentFirstName, consentLastName, consentEmail, consentAgreedToTerms])

  useEffect(() => {
    if (
      view !== 'forms' ||
      !kioskAccessToken ||
      assignmentsFetchedRef.current
    ) {
      return
    }
    assignmentsFetchedRef.current = true
    setAssignmentsLoading(true)
    setAssignmentsError(null)
    const { clinicId, dbName } = clinicContext
    getKioskAssignments(kioskAccessToken, {
      clinicId,
      dbName,
      patientId: kioskPatientId || undefined,
    })
      .then(({ assignments, clinic }) => {
        setKioskClinicId(
          clinic?.clinic_id ?? assignments[0]?.clinicId ?? clinicId ?? null,
        )
        setClinicContactPhone(getClinicContactPhone(clinic))
        setKioskClinicName(clinic?.clinic_name?.trim() ?? '')
        if (assignments.length === 0) {
          if (
            emptyAssignmentsView(identificationMode) === 'registration-complete'
          ) {
            setAssignmentsError(null)
            setFormEntries([])
            setSelectedFormId(null)
            setView('registration-complete')
            return
          }
          setAssignmentsError(
            'No forms found for this session. Please ask staff for assistance.',
          )
          setFormEntries([])
          setSelectedFormId(null)
          return
        }
        const entries = assignmentsToFormEntries(assignments)
        initialFieldValuesRef.current = Object.fromEntries(
          entries.map((entry) => [entry.assignmentId, entry.fieldValues]),
        )
        setFormEntries(entries)
        setSelectedFormId(entries[0]?.assignmentId ?? null)
        setSidepanelOpen(entries.length > 1)
      })
      .catch(
        (err: {
          response?: { data?: { message?: string } }
          message?: string
        }) => {
          const msg =
            err?.response?.data?.message ??
            err?.message ??
            'Unable to load your forms. Please try again or ask staff for assistance.'
          setAssignmentsError(msg)
        },
      )
      .finally(() => {
        setAssignmentsLoading(false)
      })
  }, [
    view,
    kioskAccessToken,
    kioskPatientId,
    clinicContext,
    identificationMode,
  ])

  const selectedEntry =
    selectedFormId != null
      ? (formEntries.find((e) => e.assignmentId === selectedFormId) ?? null)
      : null

  const handleSelectForm = useCallback(
    (nextAssignmentId: string): void => {
      setVisitedAssignmentIds((prev) => {
        if (selectedFormId == null || selectedFormId === nextAssignmentId) {
          return prev
        }
        return new Set([...Array.from(prev), selectedFormId])
      })
      setSelectedFormId(nextAssignmentId)
      setSidepanelOpen(false)
    },
    [selectedFormId],
  )

  const validationByAssignmentId = useMemo(() => {
    const map: Record<
      string,
      ReturnType<typeof validateSchemaFormSubmission>
    > = {}
    for (const entry of formEntries) {
      map[entry.assignmentId] = validateSchemaFormSubmission(
        entry.groups,
        entry.fieldValues,
      )
    }
    return map
  }, [formEntries])

  const isFormComplete = useCallback(
    (assignmentId: string): boolean =>
      validationByAssignmentId[assignmentId]?.isValid === true &&
      (visitedAssignmentIds.has(assignmentId) ||
        assignmentId === selectedFormId),
    [validationByAssignmentId, visitedAssignmentIds, selectedFormId],
  )

  const completedFormsCount = useMemo(
    (): number =>
      formEntries.filter((entry) => isFormComplete(entry.assignmentId)).length,
    [formEntries, isFormComplete],
  )
  const allFormsCompleted =
    formEntries.length > 0 && completedFormsCount === formEntries.length
  const remainingFormsCount = formEntries.length - completedFormsCount

  const hasUnsavedInput = useMemo((): boolean => {
    return formEntries.some((entry) => {
      const initial = initialFieldValuesRef.current[entry.assignmentId] ?? {}
      const fieldIds = new Set([
        ...Object.keys(initial),
        ...Object.keys(entry.fieldValues),
      ])
      return Array.from(fieldIds).some(
        (fieldId) =>
          !fieldValuesEqual(initial[fieldId], entry.fieldValues[fieldId]),
      )
    })
  }, [formEntries])

  const allFormsValid = useMemo((): boolean => {
    if (formEntries.length === 0) return false
    return formEntries.every(
      (e) => validationByAssignmentId[e.assignmentId]?.isValid === true,
    )
  }, [formEntries, validationByAssignmentId])

  const COMPLETION_AUTO_RETURN_MS = 15000
  useEffect(() => {
    if (!allDone || view !== 'forms') return
    const timeoutId = window.setTimeout(() => {
      setView('patient-type')
    }, COMPLETION_AUTO_RETURN_MS)
    return () => window.clearTimeout(timeoutId)
  }, [allDone, view])

  useEffect(() => {
    if (view !== 'registration-complete') return
    const timeoutId = window.setTimeout(() => {
      setView('patient-type')
    }, COMPLETION_AUTO_RETURN_MS)
    return () => window.clearTimeout(timeoutId)
  }, [view])

  const returnToPatientType = useCallback((): void => {
    setView('patient-type')
  }, [])

  useIdleReset(view === 'new-patient', IDLE_RESET_MS, returnToPatientType)
  useIdleReset(view === 'assignment-error', IDLE_RESET_MS, returnToPatientType)
  useIdleReset(
    view === 'forms' && submitPhase !== 'submitting',
    IDLE_RESET_MS,
    returnToPatientType,
  )

  useEffect(() => {
    if (view === 'patient-type') {
      clearKioskPatientSession()
      assignmentsFetchedRef.current = false
      setAllDone(false)
      setIdentificationMode(null)
      setAssignmentsError(null)
      setSubmitPhase(null)
      setSubmitMessage('')
      setCodeDigits(Array.from({ length: OTP_BOX_COUNT }, () => ''))
      setVerifyError('')
      setConsentFirstName('')
      setConsentLastName('')
      setConsentEmail('')
      setConsentAgreedToTerms(false)
      setConsentData(null)
      setConsentErrors({})
      setKioskAccessToken(null)
      setKioskPatientId('')
      setCreatedPatientId('')
      setFormEntries([])
      setVisitedAssignmentIds(new Set())
      setShowFormValidation(false)
      setSelectedFormId(null)
      setKioskClinicId(null)
      setClinicContactPhone('')
      setKioskClinicName('')
      setLeaveConfirmOpen(false)
      setSidepanelOpen(false)
      initialFieldValuesRef.current = {}
      successfulSubmissionCountRef.current = 0
      failedSubmissionCountRef.current = 0
      setRegistrationNotice('')
    }
  }, [view])

  const handleFieldChange = (
    aId: string,
    fId: string,
    val: SchemaFormFieldValue,
  ): void => {
    setFormEntries((prev) =>
      prev.map((e) =>
        e.assignmentId === aId
          ? { ...e, fieldValues: { ...e.fieldValues, [fId]: val } }
          : e,
      ),
    )
  }

  const handleSubmitForms = useCallback((): void => {
    if (!kioskAccessToken) {
      setSubmitPhase('error')
      setSubmitMessage('Session expired. Please sign in again with your code.')
      return
    }
    if (allDone || formEntries.length === 0) return
    if (!allFormsValid) {
      setShowFormValidation(true)
      const firstInvalid = formEntries.find(
        (e) => validationByAssignmentId[e.assignmentId]?.isValid === false,
      )
      if (firstInvalid) {
        handleSelectForm(firstInvalid.assignmentId)
        setSidepanelOpen(false)
      }
      return
    }
    setSubmitPhase('submitting')
    const clinicId =
      kioskClinicId ??
      (clinicContext.clinicId !== '' ? clinicContext.clinicId : null)
    if (!clinicId) {
      setSubmitPhase('error')
      setSubmitMessage(
        'Clinic context is missing. Please ask staff for assistance.',
      )
      return
    }
    successfulSubmissionCountRef.current = 0
    failedSubmissionCountRef.current = 0
    setSubmitMessage('Saving your forms...')
    const dbName =
      clinicContext.dbName !== '' ? clinicContext.dbName : undefined
    const accessToken = kioskAccessToken
    const entries = [...formEntries]
    void (async (): Promise<void> => {
      for (const entry of entries) {
        try {
          const patientName =
            entry.patientName ??
            (consentData
              ? `${consentData.firstName} ${consentData.lastName}`.trim()
              : undefined)
          const auditContext: KioskAuditContext = {
            clinic_id: clinicId,
            patient_id: kioskPatientId || undefined,
            patient_name: patientName ?? undefined,
            appointment_id: entry.appointmentId ?? undefined,
            form_template_id: entry.formTemplateId,
            consent_accepted: consentData?.agreedToTerms ?? false,
            terms_version: consentData?.termsVersion,
            accepted_at: consentData?.acceptedAt,
            signature_method: 'drawn_or_typed',
            client_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            session_id: sessionStorage.getItem('kiosk_session_id') ?? undefined,
          }
          await completeKioskAssignment(
            accessToken,
            entry.assignmentId,
            {
              id: entry.assignmentId,
              name: entry.name,
              fields: formDataFieldsFromSchemaAndValues(
                entry.groups,
                entry.fieldValues,
              ),
            },
            { clinicId, dbName },
            auditContext,
          )
          successfulSubmissionCountRef.current += 1
        } catch {
          failedSubmissionCountRef.current += 1
        }
      }
      const outcome = kioskSubmitOutcome(
        successfulSubmissionCountRef.current,
        failedSubmissionCountRef.current,
        entries.length,
      )
      setSubmitPhase(outcome.phase)
      setSubmitMessage(outcome.message)
      if (outcome.phase === 'success') {
        clearKioskPatientSession()
        setAllDone(true)
        setFormEntries([])
        setSelectedFormId(null)
      }
    })()
  }, [
    kioskAccessToken,
    formEntries,
    allDone,
    allFormsValid,
    kioskClinicId,
    clinicContext.clinicId,
    clinicContext.dbName,
    handleSelectForm,
    validationByAssignmentId,
    kioskPatientId,
    consentData,
  ])

  const handleFormsBack = useCallback((): void => {
    if (allDone || !hasUnsavedInput) {
      clearKioskPatientSession()
      setView(SKIP_CONSENT_SCREEN ? 'otp' : 'consent')
      return
    }
    setLeaveConfirmOpen(true)
  }, [allDone, hasUnsavedInput])

  const contactPhone = clinicContactPhone.trim()
  const formsSupportMessage = (
    <>
      {contactPhone
        ? 'Please ask the front desk or call '
        : 'Please ask the front desk for assistance with '}
      {contactPhone && (
        <span className="kiosk-form-forms-message__contact">
          {contactPhone}
        </span>
      )}
      {contactPhone ? ' for ' : ''}
      any questions and concerns regarding the forms. Thank you.
    </>
  )

  const singleForm = formEntries.length === 1

  return (
    <div className="kiosk-form-page">
      <div className="kiosk-form-container">
        {view === 'setup' || !isKioskReady ? (
          <>
            <div className="kiosk-form-title">Kiosk setup</div>
            <div className="kiosk-form-body kiosk-form-body-otp kiosk-setup">
              <div className="kiosk-setup__heading">
                <div className="kiosk-setup__eyebrow">Activate this kiosk</div>
                <h1 className="kiosk-setup__title">Staff Sign-in</h1>
              </div>
              <p className="kiosk-setup__description">
                Staff must bind this device to a clinic. Existing patients can
                use OTP, and staff activation enables new-patient registration.
              </p>
              {staffSessionAvailable && (
                <div className="kiosk-setup__form">
                  <DsButton
                    variant="primary"
                    size="md"
                    fullWidth
                    onClick={handleActivateFromStaffSession}
                    disabled={setupLoading}
                  >
                    Activate with current staff session
                  </DsButton>
                </div>
              )}
              {pendingClinicActivation ? (
                <div className="kiosk-setup__form">
                  <Select
                    label="Select clinic"
                    className="kiosk-clinic-select"
                    options={pendingClinicActivation.clinics.map((clinic) => ({
                      label: clinic.clinicName,
                      value: clinic.clinicId,
                    }))}
                    value={setupClinicId}
                    placeholder="Select a clinic"
                    error={Boolean(setupError)}
                    helperText={setupError || undefined}
                    onChange={(value): void => {
                      setSetupClinicId(Array.isArray(value) ? '' : value)
                      setSetupError('')
                    }}
                  />
                  <DsButton
                    variant="primary"
                    size="md"
                    fullWidth
                    onClick={handleClinicActivation}
                  >
                    Activate kiosk
                  </DsButton>
                  <DsButton
                    variant="secondary"
                    size="md"
                    fullWidth
                    onClick={() => {
                      setPendingClinicActivation(null)
                      setSetupClinicId('')
                      setSetupError('')
                    }}
                  >
                    Use different account
                  </DsButton>
                </div>
              ) : (
                <form
                  className="kiosk-setup__form"
                  onSubmit={(e) => {
                    e.preventDefault()
                    handleSetupLogin()
                  }}
                >
                  <DsInputField
                    label="Username"
                    variant="text"
                    placeholder="Enter username"
                    fullWidth
                    value={setupUsername}
                    onChange={(e) => {
                      setSetupUsername(e.target.value)
                      setSetupError('')
                    }}
                    onClear={() => {
                      setSetupUsername('')
                      setSetupError('')
                    }}
                  />
                  <DsInputField
                    label="Password"
                    variant="password"
                    placeholder="Enter password"
                    fullWidth
                    value={setupPassword}
                    onChange={(e) => {
                      setSetupPassword(e.target.value)
                      setSetupError('')
                    }}
                    onClear={() => {
                      setSetupPassword('')
                      setSetupError('')
                    }}
                  />
                  {setupError && (
                    <div className="kiosk-form-verify-error" role="alert">
                      {setupError}
                    </div>
                  )}
                  <DsButton
                    variant="primary"
                    size="md"
                    fullWidth
                    type="submit"
                    disabled={setupLoading}
                  >
                    {setupLoading ? 'Activating...' : 'Activate kiosk'}
                  </DsButton>
                </form>
              )}
            </div>
          </>
        ) : view === 'patient-type' ? (
          <>
            <div className="kiosk-form-title">
              Patient Forms Access
              {isKioskDeviceMode() && (
                <button
                  type="button"
                  className="kiosk-form-deactivate"
                  onClick={handleLogoutKiosk}
                  aria-label="Log out"
                >
                  <i className="ti ti-logout" aria-hidden="true" />
                </button>
              )}
            </div>
            <div className="kiosk-form-body kiosk-form-body-otp kiosk-setup kiosk-patient-type">
              <div className="kiosk-setup__heading">
                <div className="kiosk-setup__eyebrow">Welcome</div>
                <h1 className="kiosk-setup__title">How can we help?</h1>
              </div>
              <p className="kiosk-setup__description">
                Choose option that describes you
              </p>
              {registrationNotice && (
                <div className="kiosk-form-registration-notice" role="status">
                  {registrationNotice}
                </div>
              )}
              <div className="kiosk-patient-type__choices">
                <SelectionBox
                  title="New patient"
                  description="Create your patient record"
                  className="kiosk-patient-type__choice"
                  showSelectIndicator={false}
                  onSelect={handleNewPatientChoice}
                />
                <SelectionBox
                  title="Existing patient"
                  description="Continue with your 6-digit code"
                  className="kiosk-patient-type__choice"
                  showSelectIndicator={false}
                  onSelect={() => {
                    setRegistrationNotice('')
                    setView('otp')
                  }}
                />
              </div>
            </div>
          </>
        ) : view === 'new-patient' ? (
          <>
            <div className="kiosk-form-title kiosk-form-title-with-back">
              <button
                type="button"
                className="kiosk-form-back"
                onClick={() => setView('patient-type')}
                aria-label="Cancel registration and start over"
              >
                <i className="mgc mgc_left_line" />
                Start over
              </button>
              New patient registration
            </div>
            <div className="kiosk-form-body kiosk-form-body-registration">
              <KioskPatientRegistration
                clinicId={clinicContext.clinicId}
                accessToken={registrationAccessToken}
                actorId={registrationActorId}
                onCancel={() => setView('patient-type')}
                onCreated={handleRegistrationCreated}
                onDuplicate={handleRegistrationDuplicate}
              />
            </div>
          </>
        ) : view === 'registration-complete' ? (
          <>
            <div className="kiosk-form-title">Patient Registration</div>
            <div className="kiosk-form-body kiosk-form-body-centered">
              <div className="kiosk-form-submit-flow kiosk-registration-complete">
                <div className="kiosk-form-submit-heading">
                  Registration complete
                </div>
                <div
                  className="kiosk-form-success-container"
                  role="status"
                  aria-live="polite"
                >
                  <i
                    className="mgc mgc_check_circle_fill kiosk-form-success-icon"
                    aria-hidden="true"
                  />
                  <div className="kiosk-form-success-text">
                    Your patient record was created successfully.
                  </div>
                  <div className="kiosk-form-success-note">
                    No additional forms are assigned right now. Please return
                    this device to the front desk.
                  </div>
                  <DsButton
                    variant="primary"
                    size="lg"
                    className="kiosk-form-got-it-btn"
                    onClick={() => setView('patient-type')}
                  >
                    Finish
                  </DsButton>
                </div>
              </div>
            </div>
          </>
        ) : view === 'preparing-forms' ? (
          <>
            <div className="kiosk-form-title">Patient Registration</div>
            <div className="kiosk-form-body kiosk-form-body-centered">
              <div
                className="kiosk-form-submit-flow kiosk-preparing-forms"
                role="status"
                aria-live="polite"
              >
                <span
                  className="kiosk-preparing-forms__spinner"
                  aria-hidden="true"
                />
                <div className="kiosk-form-submit-heading">
                  Preparing your forms
                </div>
                <div className="kiosk-form-success-note">
                  Your patient record was created. Please wait while we prepare
                  the forms for you to sign.
                </div>
              </div>
            </div>
          </>
        ) : view === 'assignment-error' ? (
          <>
            <div className="kiosk-form-title">Patient Registration</div>
            <div className="kiosk-form-body kiosk-form-body-centered">
              <div className="kiosk-form-submit-flow" role="alert">
                <i
                  className="mgc mgc_alert_fill kiosk-form-assignment-error-icon"
                  aria-hidden="true"
                />
                <div className="kiosk-form-submit-heading">
                  We could not prepare your forms
                </div>
                <div className="kiosk-form-success-note">
                  Your patient record was created successfully. Please retry or
                  ask the front desk for assistance. Retrying will not create a
                  second patient record.
                </div>
                <div className="kiosk-form-otp-actions">
                  <DsButton
                    variant="primary"
                    size="lg"
                    onClick={() =>
                      void prepareRegistrationForms(createdPatientId)
                    }
                    disabled={!createdPatientId}
                  >
                    Retry
                  </DsButton>
                  <DsButton
                    variant="secondary"
                    size="lg"
                    onClick={() => setView('patient-type')}
                  >
                    Start over
                  </DsButton>
                </div>
              </div>
            </div>
          </>
        ) : view === 'otp' ? (
          <>
            <div className="kiosk-form-title kiosk-form-title-with-back">
              <button
                type="button"
                className="kiosk-form-back"
                onClick={() => setView('patient-type')}
                aria-label="Back to welcome screen"
              >
                <i className="mgc mgc_left_line" aria-hidden="true" />
                Back
              </button>
              Patient Forms Access
              {isKioskDeviceMode() && (
                <button
                  type="button"
                  className="kiosk-form-deactivate"
                  onClick={handleLogoutKiosk}
                  aria-label="Log out"
                >
                  <i className="ti ti-logout" aria-hidden="true" />
                </button>
              )}
            </div>
            <div className="kiosk-form-body kiosk-form-body-otp kiosk-setup">
              <div className="kiosk-setup__heading">
                <div className="kiosk-setup__eyebrow">Patient Forms Access</div>
                <h1 className="kiosk-setup__title">OTP Sign-in</h1>
              </div>
              <p className="kiosk-setup__description">
                Please enter the provided OTP or ask for front-desk assistance
              </p>
              {registrationNotice && (
                <div className="kiosk-form-registration-notice" role="status">
                  {registrationNotice}
                </div>
              )}
              <div
                className="kiosk-otp-field"
                role="group"
                aria-labelledby="kiosk-otp-label"
              >
                <div id="kiosk-otp-label" className="kiosk-otp-field__label">
                  Enter your OTP here
                </div>
                <div className="kiosk-form-otp-boxes">
                  {codeDigits.map((digit, i) => (
                    <input
                      key={i}
                      name={`kiosk-otp-${i}`}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleCodeDigitChange(i, e.target.value)}
                      onKeyDown={(e) => handleCodeKeyDown(i, e)}
                      onPaste={handleCodePaste}
                      className="kiosk-form-otp-box kiosk-form-otp-input"
                      aria-label={`Digit ${i + 1}`}
                    />
                  ))}
                </div>
              </div>
              {verifyError && (
                <div className="kiosk-form-verify-error">{verifyError}</div>
              )}
              <div className="kiosk-otp-actions">
                <DsButton
                  variant="primary"
                  size="md"
                  fullWidth
                  onClick={handleVerifySubmit}
                  disabled={verifying}
                >
                  {verifying ? 'Signing in...' : 'Continue'}
                </DsButton>
              </div>
              {verifying && (
                <div className="kiosk-form-loading-container">
                  <i className="mgc mgc_loading_line kiosk-form-loading-icon" />
                </div>
              )}
            </div>
          </>
        ) : view === 'consent' ? (
          <>
            <div className="kiosk-form-title kiosk-form-title-with-back">
              <button
                type="button"
                className="kiosk-form-back"
                onClick={() => setView('otp')}
                aria-label="Back"
              >
                <i className="mgc mgc_left_line" />
                Back
              </button>
              Patient Forms Access
            </div>
            <div className="kiosk-form-body kiosk-form-body-consent">
              <div className="kiosk-form-body-title">Patient Forms Access</div>
              <div className="kiosk-form-consent-heading">
                Consent &amp; Information
              </div>
              <div className="kiosk-form-consent-subtitle">
                Please provide your information and accept the terms to continue
                to your forms.
              </div>
              <form className="kiosk-form-consent-form">
                <div className="kiosk-form-consent-fields">
                  <DsInputField
                    label="First name"
                    variant="text"
                    fullWidth
                    value={consentFirstName}
                    onChange={(e) => {
                      setConsentFirstName(e.target.value)
                      setConsentErrors((prev) => ({
                        ...prev,
                        firstName: undefined,
                      }))
                    }}
                    placeholder="Enter your first name"
                    error={Boolean(consentErrors.firstName)}
                    helperText={consentErrors.firstName}
                  />
                  <DsInputField
                    label="Last name"
                    variant="text"
                    fullWidth
                    value={consentLastName}
                    onChange={(e) => {
                      setConsentLastName(e.target.value)
                      setConsentErrors((prev) => ({
                        ...prev,
                        lastName: undefined,
                      }))
                    }}
                    placeholder="Enter your last name"
                    error={Boolean(consentErrors.lastName)}
                    helperText={consentErrors.lastName}
                  />
                  <DsInputField
                    label="Email"
                    variant="email"
                    fullWidth
                    value={consentEmail}
                    onChange={(e) => {
                      setConsentEmail(e.target.value)
                      setConsentErrors((prev) => ({
                        ...prev,
                        email: undefined,
                      }))
                    }}
                    placeholder="Enter your email"
                    error={Boolean(consentErrors.email)}
                    helperText={consentErrors.email}
                  />
                </div>
                <div className="kiosk-form-consent-terms">
                  <Checkbox
                    id="kiosk-consent-terms-checkbox"
                    checked={consentAgreedToTerms}
                    onChange={(e) => {
                      setConsentAgreedToTerms(e.target.checked)
                      setConsentErrors((prev) => ({
                        ...prev,
                        terms: undefined,
                      }))
                    }}
                    label={
                      <>
                        I agree to the{' '}
                        <LinkButton
                          type="button"
                          className="kiosk-form-consent-terms-link"
                          size="sm"
                          underline
                          onClick={(e) => {
                            e.preventDefault()
                            setTermsModalOpen(true)
                          }}
                        >
                          Terms and Conditions
                        </LinkButton>
                      </>
                    }
                    className="kiosk-form-consent-checkbox"
                  />
                  {consentErrors.terms && (
                    <div className="kiosk-form-consent-error" role="alert">
                      {consentErrors.terms}
                    </div>
                  )}
                </div>
                <div className="kiosk-form-consent-actions">
                  <DsButton
                    variant="primary"
                    size="lg"
                    onClick={handleConsentContinue}
                    disabled={!consentFormValid}
                  >
                    Continue
                  </DsButton>
                </div>
              </form>
            </div>
            <Modal
              open={termsModalOpen}
              onClose={() => setTermsModalOpen(false)}
              size="sm"
              title="Terms and conditions"
              showFooter={false}
              className="kiosk-form-terms-modal"
              bodyClassName="kiosk-form-terms-modal-body"
            >
              <>
                <p>
                  By using this kiosk to complete your forms, you confirm that
                  the information you provide is accurate and that you consent
                  to the practice&apos;s policies regarding the collection and
                  use of your personal and health information.
                </p>
                <p>
                  Please read the full terms provided by the practice. If you
                  have questions, contact the front desk before proceeding.
                </p>
              </>
            </Modal>
          </>
        ) : (
          <>
            <div className="kiosk-form-title kiosk-form-title-with-back">
              <button
                type="button"
                className="kiosk-form-back"
                onClick={handleFormsBack}
                aria-label="Back"
              >
                <i className="mgc mgc_left_line" />
                Back
              </button>
              Patient Forms Access
            </div>
            <div className="kiosk-form-body kiosk-form-body-forms">
              {allDone ? (
                <div className="kiosk-form-submit-flow">
                  <div className="kiosk-form-body-title">
                    Patient Forms Access
                  </div>
                  <div className="kiosk-form-submitting">
                    <img
                      className="kiosk-form-success__icon"
                      src={checkmarkIcon}
                      alt=""
                    />
                    <div className="kiosk-form-submit-heading">Success!</div>
                    <p className="kiosk-form-submitting__note">
                      Your forms have been submitted and saved in our records.
                    </p>
                  </div>
                  <div className="kiosk-form-success__action">
                    <DsButton
                      variant="primary"
                      size="md"
                      fullWidth
                      onClick={() => setView('patient-type')}
                    >
                      Okay
                    </DsButton>
                  </div>
                </div>
              ) : submitPhase !== null ? (
                <div className="kiosk-form-submit-flow">
                  <div className="kiosk-form-body-title">
                    Patient Forms Access
                  </div>
                  {submitPhase === 'submitting' ? (
                    <div
                      className="kiosk-form-submitting"
                      role="status"
                      aria-live="polite"
                    >
                      <img
                        className="kiosk-form-submitting__spinner"
                        src={progressActivityIcon}
                        alt=""
                      />
                      <div className="kiosk-form-submit-heading">
                        Submitting forms...
                      </div>
                      <p className="kiosk-form-submitting__note">
                        It won’t take long
                      </p>
                    </div>
                  ) : submitPhase === 'error' || submitPhase === 'partial' ? (
                    <>
                      <div className="kiosk-form-submit-heading">
                        {submitPhase === 'error'
                          ? 'Submission issue'
                          : 'Partially submitted'}
                      </div>
                      <div className="kiosk-form-success-container">
                        <div className="kiosk-form-verify-error">
                          {submitMessage}
                        </div>
                        <DsButton
                          variant="primary"
                          size="lg"
                          className="kiosk-form-got-it-btn"
                          onClick={() => {
                            setSubmitPhase(null)
                            setSubmitMessage('')
                          }}
                        >
                          Got it
                        </DsButton>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="kiosk-form-submitting">
                        <img
                          className="kiosk-form-success__icon"
                          src={checkmarkIcon}
                          alt=""
                        />
                        <div className="kiosk-form-submit-heading">
                          Success!
                        </div>
                        <p className="kiosk-form-submitting__note">
                          Your forms have been submitted and saved in our
                          records.
                        </p>
                      </div>
                      <div className="kiosk-form-success__action">
                        <DsButton
                          variant="primary"
                          size="md"
                          fullWidth
                          onClick={() => {
                            setSubmitPhase(null)
                            setSubmitMessage('')
                          }}
                        >
                          Okay
                        </DsButton>
                      </div>
                    </>
                  )}
                </div>
              ) : assignmentsLoading ? (
                <div className="kiosk-form-body-title">
                  <div className="kiosk-form-loading-container">
                    <i className="mgc mgc_loading_line kiosk-form-loading-icon" />
                    <div className="kiosk-form-loading-text">
                      Loading your forms...
                    </div>
                  </div>
                </div>
              ) : assignmentsError ? (
                <div className="kiosk-form-body-title">
                  <div className="kiosk-form-verify-error">
                    {assignmentsError}
                  </div>
                </div>
              ) : formEntries.length === 0 ? (
                <div className="kiosk-form-body-title">
                  <div className="kiosk-form-forms-message-inner">
                    No forms are currently assigned to you. Please check with
                    the front desk if you were expecting forms.
                  </div>
                </div>
              ) : (
                <>
                  {!singleForm && (
                    <div
                      className={`kiosk-form-forms-sidepanel ${sidepanelOpen ? 'open' : ''}`}
                      aria-hidden={!sidepanelOpen}
                    >
                      <div className="kiosk-form-forms-sidepanel-inner">
                        {formEntries.map((entry) => {
                          const complete = isFormComplete(entry.assignmentId)
                          const invalid = showFormValidation && !complete
                          const itemClass = [
                            'kiosk-form-review-form-item',
                            selectedFormId === entry.assignmentId
                              ? 'active'
                              : '',
                            complete
                              ? 'kiosk-form-review-form-item--complete'
                              : '',
                            invalid
                              ? 'kiosk-form-review-form-item--invalid'
                              : '',
                          ]
                            .filter(Boolean)
                            .join(' ')
                          return (
                            <div
                              key={entry.assignmentId}
                              className={itemClass}
                              onClick={() =>
                                handleSelectForm(entry.assignmentId)
                              }
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  handleSelectForm(entry.assignmentId)
                                }
                              }}
                            >
                              <span>{entry.name}</span>
                              {complete ? (
                                <i
                                  className="mgc mgc_check_circle_fill"
                                  aria-hidden
                                />
                              ) : invalid ? (
                                <span className="kiosk-form-review-form-status">
                                  <i
                                    className="mgc mgc_warning_line"
                                    aria-hidden
                                  />
                                  <small>Required fields missing</small>
                                </span>
                              ) : (
                                <i className="mgc mgc_right_line" />
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                  {!singleForm && sidepanelOpen && (
                    <button
                      type="button"
                      className="kiosk-form-forms-sidepanel-backdrop"
                      onClick={() => setSidepanelOpen(false)}
                      aria-label="Close forms list"
                    />
                  )}
                  <div className="kiosk-form-forms-header">
                    {singleForm ? (
                      <div className="kiosk-form-forms-header-left kiosk-form-forms-header-left--static">
                        <span>{formEntries[0]?.name}</span>
                        <span
                          className={`kiosk-form-forms-counter${allFormsCompleted ? ' kiosk-form-forms-counter--done' : ''}`}
                          aria-live="polite"
                        >
                          {allFormsCompleted && (
                            <i
                              className="mgc mgc_check_circle_fill"
                              aria-hidden
                            />
                          )}
                          {completedFormsCount}/{formEntries.length} completed
                        </span>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="kiosk-form-forms-header-left"
                        onClick={() => setSidepanelOpen((open) => !open)}
                        title={
                          sidepanelOpen ? 'Close forms list' : 'Open forms list'
                        }
                        aria-label={
                          sidepanelOpen ? 'Close forms list' : 'Open forms list'
                        }
                      >
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden
                        >
                          <path
                            d="M3 18V16H15V18H3ZM3 13V11H21V13H3ZM3 8V6H21V8H3Z"
                            fill="white"
                          />
                        </svg>
                        <span>Forms & Documents</span>
                        <span
                          className={`kiosk-form-forms-counter${allFormsCompleted ? ' kiosk-form-forms-counter--done' : ''}`}
                          aria-live="polite"
                        >
                          {allFormsCompleted && (
                            <i
                              className="mgc mgc_check_circle_fill"
                              aria-hidden
                            />
                          )}
                          {completedFormsCount}/{formEntries.length} completed
                        </span>
                      </button>
                    )}
                    <DsButton
                      variant="secondary"
                      size="sm"
                      onClick={handleSubmitForms}
                      title={
                        !allFormsValid
                          ? 'Please complete all required fields (including signatures) before submitting.'
                          : undefined
                      }
                    >
                      {allFormsCompleted
                        ? 'Submit Forms'
                        : `Submit (${remainingFormsCount} remaining)`}
                    </DsButton>
                  </div>
                  {showFormValidation && !allFormsValid && (
                    <div className="kiosk-form-verify-error" role="alert">
                      Please complete all required fields (signatures are
                      required) before submitting.
                    </div>
                  )}
                  <div className="kiosk-form-forms-message">
                    <div className="kiosk-form-forms-message-inner kiosk-form-forms-message-inner--notice">
                      <p className="kiosk-form-forms-message__title">
                        Sign and complete the forms before we proceed with your
                        appointment.
                      </p>
                      <p className="kiosk-form-forms-message__text">
                        {formsSupportMessage}
                      </p>
                    </div>
                  </div>
                  <div className="kiosk-form-forms-content">
                    {selectedEntry ? (
                      <SchemaFormRenderer
                        groups={selectedEntry.groups}
                        formTitle={selectedEntry.name}
                        fieldValues={selectedEntry.fieldValues}
                        readOnly={false}
                        richTextReadOnly
                        showValidation={showFormValidation}
                        validationErrors={
                          validationByAssignmentId[selectedEntry.assignmentId]
                            ?.errorsByFieldId ?? {}
                        }
                        onFieldChange={(fieldId, value) =>
                          handleFieldChange(
                            selectedEntry.assignmentId,
                            fieldId,
                            value,
                          )
                        }
                        patientName={
                          selectedEntry.patientName ??
                          (consentData
                            ? `${consentData.firstName} ${consentData.lastName}`.trim()
                            : undefined)
                        }
                        useDesignSystemTextArea
                        brandHeader={{
                          logoSrc: '/logo512.png',
                          clinicName: kioskClinicName,
                        }}
                      />
                    ) : (
                      <div className="kiosk-form-forms-placeholder">
                        Select a form from the list
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
      <ConfirmationModal
        show={leaveConfirmOpen}
        onHide={() => setLeaveConfirmOpen(false)}
        title="Leave forms?"
        body={
          <p className="mb-0">
            Your answers will be lost if you leave this screen.
          </p>
        }
        footer={
          <>
            <DsButton
              variant="secondary"
              size="md"
              onClick={() => setLeaveConfirmOpen(false)}
            >
              Stay
            </DsButton>
            <DsButton
              variant="primary"
              size="md"
              onClick={() => {
                setLeaveConfirmOpen(false)
                setView(SKIP_CONSENT_SCREEN ? 'otp' : 'consent')
              }}
            >
              Leave
            </DsButton>
          </>
        }
      />
    </div>
  )
}

export default KioskForm
